import { TenantIds } from '@config/tenant-id';
import { EnumSelect } from '@summit/core';

export enum AdjustmentType {
  Manual = 0,
  Weekly = 1,
  Daily = 2,
  PerLeg = 3,
  PerLegWithDH = 4,
  DhPercentage = 5,
  SoloDhByTrailerNone = 6,
  SoloDhByTrailerSingle = 7,
  SoloDhByTrailerDouble = 8,
  SoloDhByTrailerTriple = 9,
  TeamDhByTrailerNone = 10,
  TeamDhByTrailerSingle = 11,
  TeamDhByTrailerDouble = 12,
  TeamDhByTrailerTriple = 13,
  HourlyPayForDepartureDelays = 14,
  HourlyPayForRerouteDelays = 15,
  GrossNetFuelPay = 16,
  MileageSoloCorrection = 20,
  RouteCorrection = 21,
  SpotCorrection = 22,
  PWCorrection = 23,
  MileageTeamCorrection = 24,
  Incentive = 30,
  WageExtra = 31,
  DaysGuaranteed = 32,
  DayMilesThreshold = 33,
  DayStopThreshold = 34,
  DaySpotThreshold = 35,
  HourlyPayForHourLog = 36,
  EmployeeAdjCorrection = 37,
  MileageSpotCorrection = 38,
  DayPay6th = 39,
  Safety = 40,
  SafetySolo = 41,
  SafetyTeam = 42,
  SafetySpot = 49,
  AvailabilitySolo = 43,
  AvailabilityTeam = 44,
  LegWage = 45,
  IncludeSoloMiles = 46,
  IncludeTeamMiles = 47,
  Overtime = 48,
  Orientation = 50,
  DayPayCorrection = 51,
  MileageSoloRuleCorrection = 52,
  MileageTeamRuleCorrection = 53,
  Weekend = 54,
  Trainer = 55,
  Trainee = 56,
  AvailabilitySpot = 57,
  BlockFurtherRules = 58,
  DhCorrection = 59,
  DailyOverride = 60,
  WageExtraDH = 61,
  DayPay6thDayCorrection = 62,
  HourlyCorrection = 63,
  DayPayIncremental = 64
}

export const AdjustmentTypeMapping: Record<AdjustmentType, string> = {
  [AdjustmentType.Manual]: 'Manual',
  [AdjustmentType.Weekly]: 'Weekly',
  [AdjustmentType.Daily]: 'Daily',
  [AdjustmentType.PerLeg]: 'Per Leg Pay',
  [AdjustmentType.PerLegWithDH]: 'Per Leg with DH',
  [AdjustmentType.DhPercentage]: 'Percentage of DH',
  [AdjustmentType.SoloDhByTrailerNone]: 'Per Solo Bobtail',
  [AdjustmentType.SoloDhByTrailerSingle]: 'Per Solo Single DH',
  [AdjustmentType.SoloDhByTrailerDouble]: 'Per Solo Double DH',
  [AdjustmentType.SoloDhByTrailerTriple]: 'Per Solo Triple DH',
  [AdjustmentType.TeamDhByTrailerNone]: 'Per Team Bobtail',
  [AdjustmentType.TeamDhByTrailerSingle]: 'Per Team Single DH',
  [AdjustmentType.TeamDhByTrailerDouble]: 'Per Team Double DH',
  [AdjustmentType.TeamDhByTrailerTriple]: 'Per Team Triple DH',
  [AdjustmentType.HourlyPayForDepartureDelays]: 'Hourly Pay for Delays',
  [AdjustmentType.HourlyPayForRerouteDelays]: 'Hourly Pay for ReRoutes',
  [AdjustmentType.GrossNetFuelPay]: '% Gross Pay',
  [AdjustmentType.MileageSoloCorrection]: 'Solo Mileage Correction',
  [AdjustmentType.RouteCorrection]: 'Route Pay Correction',
  [AdjustmentType.SpotCorrection]: 'Spot Pay Correction',
  [AdjustmentType.PWCorrection]: 'Prior Week Correction',
  [AdjustmentType.MileageTeamCorrection]: 'Team Mileage Correction',
  [AdjustmentType.Incentive]: 'Incentive',
  [AdjustmentType.WageExtra]: 'Wage',
  [AdjustmentType.DaysGuaranteed]: 'Days Guaranteed',
  [AdjustmentType.DayMilesThreshold]: 'Day Miles Threshold',
  [AdjustmentType.DayStopThreshold]: 'Day Stop Threshold',
  [AdjustmentType.DaySpotThreshold]: 'Day Spot Threshold',
  [AdjustmentType.HourlyPayForHourLog]: 'Hourly Pay for Hour Log',
  [AdjustmentType.EmployeeAdjCorrection]: 'Employee Adjustment Correction',
  [AdjustmentType.MileageSpotCorrection]: 'Spot Mileage Correction',
  [AdjustmentType.DayPay6th]: '6th Day Pay',
  [AdjustmentType.Safety]: 'Safety Pay',
  [AdjustmentType.SafetySolo]: 'Safety Solo',
  [AdjustmentType.SafetyTeam]: 'Safety Team',
  [AdjustmentType.SafetySpot]: 'Safety Spot',
  [AdjustmentType.AvailabilitySolo]: 'Availability Solo',
  [AdjustmentType.AvailabilityTeam]: 'Availability Team',
  [AdjustmentType.LegWage]: 'Leg Wage',
  [AdjustmentType.IncludeSoloMiles]: 'Included Solo Miles',
  [AdjustmentType.IncludeTeamMiles]: 'Included Team Miles',
  [AdjustmentType.Overtime]: 'Overtime',
  [AdjustmentType.Orientation]: 'Orientation',
  [AdjustmentType.DayPayCorrection]: 'Day Pay Correction',
  [AdjustmentType.MileageSoloRuleCorrection]: 'Solo Mileage Correction (Rule)',
  [AdjustmentType.MileageTeamRuleCorrection]: 'Team Mileage Correction (Rule)',
  [AdjustmentType.Weekend]: 'Weekend',
  [AdjustmentType.Trainer]: 'Trainer',
  [AdjustmentType.Trainee]: 'Trainee',
  [AdjustmentType.AvailabilitySpot]: 'Availability Spot',
  [AdjustmentType.BlockFurtherRules]: 'Block Further Rules',
  [AdjustmentType.DhCorrection]: 'DH Correction',
  [AdjustmentType.DailyOverride]: 'Daily Override',
  [AdjustmentType.WageExtraDH]: 'Wage Extra DH',
  [AdjustmentType.DayPay6thDayCorrection]: '6th Day Pay Correction',
  [AdjustmentType.HourlyCorrection]: 'Hourly Correction',
  [AdjustmentType.DayPayIncremental]: 'Day Pay (Incr.)'
};

export function GetEmployeeAdjTypes(tenantId: string): number[] {
  switch (tenantId.toUpperCase()) {
    case TenantIds.BEI:
      return EnumSelect.ToIdListFilter(AdjustmentType, [AdjustmentType.Daily, AdjustmentType.DayPay6th]);
    default:
      return EnumSelect.ToIdList(AdjustmentType);
  }
}
